/* eslint-disable canonical/filename-match-exported -- FIXME: Fix this ESLint violation! */
import uuid from '@openphone/lib/uuid'
import type { AxiosProgressEvent, AxiosRequestConfig } from 'axios'

import config from '@src/config'
import type { PostCallActivitiesRequest } from '@src/service/dto/request/activities/calls'
import type {
  DeleteConversationRequest,
  PostConversationRequest,
} from '@src/service/dto/request/conversation'
import type { PutConversationAgentStatusRequest } from '@src/service/dto/request/conversation/PutConversationAgentStatusRequest'
import type { UpdatePhoneNumberAgentRequest } from '@src/service/dto/request/phone-number/UpdatePhoneNumberAgentRequest'
import type DeleteScheduledMessageRequest from '@src/service/dto/request/scheduled-message/DeleteScheduledMessageRequest'
import type GetScheduledMessagesCountRequest from '@src/service/dto/request/scheduled-message/GetScheduledMessagesCountRequest'
import type PostScheduledMessageRequest from '@src/service/dto/request/scheduled-message/PostScheduledMessageRequest'
import type { GetScheduledMessagesByConversationRequest } from '@src/service/dto/request/scheduled-message/conversation/GetScheduledMessagesByConversationRequest'
import type PostUploadRequest from '@src/service/dto/request/upload'
import type { PostCallActivitiesResponse } from '@src/service/dto/response/activities/calls'
import type { ConversationAgentStatus } from '@src/service/dto/response/conversation/GetConversationAgentResponse'
import type { PhoneNumberAgentStatus } from '@src/service/dto/response/phone-number/GetPhoneNumberAgentStatusResponse'
import type DeleteScheduledMessageResponse from '@src/service/dto/response/scheduled-message/DeleteScheduledMessageResponse'
import type GetScheduledMessagesCountResponse from '@src/service/dto/response/scheduled-message/GetScheduledMessagesCountResponse'
import type PostScheduledMessageResponse from '@src/service/dto/response/scheduled-message/PostScheduledMessageResponse'
import type { GetScheduledMessagesByConversationResponse } from '@src/service/dto/response/scheduled-message/conversation/GetScheduledMessagesByConversationResponse'
import type {
  ActivityModel,
  CodableConversation,
  CodableMessageMedia,
  EncodableComment,
  IActivity,
} from '@src/service/model'
import type CodableTag from '@src/service/model/CodableTag'
import type ConversationParticipantCodable from '@src/service/model/ConversationParticipantCodable'
import type { ActivityEncodableReaction } from '@src/service/model/reactions'

import type Transport from '.'
import type LegacyPaginated from './lib/LegacyPaginated'
import type Paginated from './lib/Paginated'
import type { TransactionConfiguration } from './transaction'
import { HttpTransaction } from './transaction'

export type ConversationParticipantStatus = 'active' | 'away' | 'typing' | 'exited'

export type SendActivityAddress =
  | { to: string; phoneNumberId: string; directNumberId?: string | null }
  | { to: string; directNumberId: string; phoneNumberId?: string | null }

export type Viewer = {
  userId: string
  name: ConversationParticipantStatus
  effectiveAt: string
}

export type TextToSpeechVoiceType = 'standard' | 'natural-1'

interface MessagingSuggestionResult {
  suggestions: string[]
}

interface MarkdownResult {
  result: string
}

export interface IvrSettings {
  phoneNumberId?: string
  language?: string
  greeting?: string | null
  greetingUrl?: string
  defaultDestination?: string | null
  enabled?: boolean
  options?: IvrOption[]
}

export interface IvrOption {
  name?: string
  digit?: number
  phrase?: string
  destination?: string
  message?: string
  messageUrl?: string
}

type ConversationListQueryParam = {
  before?: string
  last?: number
  since?: Date
  phoneNumber?: string
  phoneNumberId?: string
  directNumberId?: string
  snoozed?: boolean
  read?: boolean
  includeDeleted?: boolean
}

type ConversationsViewersResult = {
  [key: CodableConversation['id']]: Viewer[]
} | null

export type BucketName = 'static' | 'port_request_statement' | 'port_request_signature'
export type FolderName = 'i' | 'raw' | 'processed'
export type UploadPermissions = 'private' | 'public-read'

interface UploadOptions {
  bucket?: BucketName
  folder?: FolderName
  filePermissions?: UploadPermissions
}

type PaginatedTagsRequest = {
  phoneNumberId: string
  query?: {
    maxResults?: number
    pageToken?: string
    since?: Date
    includeCallsLabeled?: boolean
  }
}

type PaginatedTagsResponse = {
  data: CodableTag[]
  totalItems: number
  nextPageToken?: string
}

export default class CommunicationClient {
  constructor(private transport: Transport) {}

  activities = {
    list: (params: {
      id?: string | null
      phoneNumberId?: string
      directNumberId?: string
      phoneNumber?: string
      before?: string | null
      last?: number
      next?: number
      inclusive?: boolean
      since?: Date
    }): Promise<LegacyPaginated<IActivity> & { conversation: CodableConversation }> => {
      return this.transport.queue(
        new HttpTransaction({
          method: 'get',
          url: `${config.COMMUNICATION_SERVICE_URL}activity`,
          query: params,
        }),
      )
    },

    bulk: (params: { ids: string[] }): Promise<IActivity[]> => {
      return this.transport.queue(
        new HttpTransaction({
          method: 'get',
          url: `${config.COMMUNICATION_SERVICE_URL}activity/bulk`,
          query: params,
        }),
      )
    },

    send: (
      params: SendActivityAddress & {
        id?: string
        body?: string | null
        media?: CodableMessageMedia[]
        conversationId?: string | null
        sendAndMarkDone?: boolean
      },
    ): Promise<{ activity: IActivity; conversation: CodableConversation }> => {
      const {
        id,
        body,
        media,
        conversationId,
        phoneNumberId,
        directNumberId,
        to,
        sendAndMarkDone,
      } = params

      const searchParams = new URLSearchParams()

      if (sendAndMarkDone) {
        searchParams.set('sendAndMarkDone', 'true')
      }

      const paramsSegment =
        [...searchParams.keys()].length > 0 ? `?${searchParams.toString()}` : ''

      return this.transport.queue(
        new HttpTransaction({
          method: 'post',
          url: `${config.COMMUNICATION_SERVICE_URL}conversation${paramsSegment}`,
          body: {
            phoneNumberId: phoneNumberId || undefined,
            directNumberId: directNumberId || undefined,
            to,
            body,
            mediaUrl: media?.map((m) => ({
              id: m.id || uuid(),
              url: m.url,
              type: m.type,
              name: m.name,
            })),
            activityId: id,
            conversationId,
          },
        }),
      )
    },

    resolve: (id: string): Promise<any> => {
      return this.transport.queue(
        new HttpTransaction({
          method: 'post',
          url: `${config.COMMUNICATION_SERVICE_URL}activity/${id}/resolve`,
          body: {},
        }),
      )
    },

    unresolve: (id: string): Promise<any> => {
      return this.transport.queue(
        new HttpTransaction({
          method: 'post',
          url: `${config.COMMUNICATION_SERVICE_URL}activity/${id}/unresolve`,
          body: {},
        }),
      )
    },

    calls: {
      list: (
        params: PostCallActivitiesRequest,
        transactionConfiguration?: TransactionConfiguration,
      ): Promise<PostCallActivitiesResponse> => {
        return this.transport.queue(
          new HttpTransaction({
            method: 'post',
            url: `${config.COMMUNICATION_SERVICE_URL}activity/calls`,
            body: params,
            ...transactionConfiguration,
          }),
        )
      },
    },
  }

  conversations = {
    list: (
      params: ConversationListQueryParam,
    ): Promise<LegacyPaginated<CodableConversation>> => {
      return this.transport.queue(
        new HttpTransaction({
          url: `${config.COMMUNICATION_SERVICE_URL}conversation`,
          query: params,
        }),
      )
    },

    create: (params: PostConversationRequest): Promise<CodableConversation> => {
      return this.transport.queue(
        new HttpTransaction({
          url: `${config.COMMUNICATION_SERVICE_URL}conversation`,
          method: 'post',
          body: params,
        }),
      )
    },

    updateName: (params: {
      name: string
      conversationId: string
    }): Promise<CodableConversation> => {
      return this.transport.queue(
        new HttpTransaction({
          url: `${config.COMMUNICATION_SERVICE_URL}conversation/${params.conversationId}`,
          method: 'put',
          body: { name: params.name },
        }),
      )
    },

    delete: ({ id }: DeleteConversationRequest): Promise<CodableConversation> => {
      return this.transport.queue(
        new HttpTransaction({
          url: `${config.COMMUNICATION_SERVICE_URL}conversation/${id}`,
          method: 'DELETE',
        }),
      )
    },

    snooze: (id: string, duration = 525949200): Promise<any> => {
      return this.transport.queue(
        new HttpTransaction({
          method: 'put',
          url: `${config.COMMUNICATION_SERVICE_URL}conversation/${id}/snooze`,
          body: { duration },
        }),
      )
    },

    unsnooze: (id: string): Promise<CodableConversation> => {
      return this.transport.queue(
        new HttpTransaction({
          method: 'put',
          url: `${config.COMMUNICATION_SERVICE_URL}conversation/${id}/unsnooze`,
        }),
      )
    },

    listUnread: (params: {
      phoneNumberIds: string[]
    }): Promise<Record<string, number>> => {
      return this.transport.queue(
        new HttpTransaction({
          url: `${config.COMMUNICATION_SERVICE_URL}conversation/unreadCount`,
          query: params,
        }),
      )
    },

    markAsRead: (id: string): Promise<CodableConversation> => {
      return this.transport.queue(
        new HttpTransaction({
          method: 'put',
          url: `${config.COMMUNICATION_SERVICE_URL}conversation/${id}/markAsRead`,
        }),
      )
    },

    markAsUnread: (id: string): Promise<CodableConversation> => {
      return this.transport.queue(
        new HttpTransaction({
          method: 'put',
          url: `${config.COMMUNICATION_SERVICE_URL}conversation/${id}/markAsUnread`,
        }),
      )
    },

    archive: (id: string): Promise<CodableConversation> => {
      return this.transport.queue(
        new HttpTransaction({
          method: 'delete',
          url: `${config.COMMUNICATION_SERVICE_URL}conversation/${id}`,
        }),
      )
    },

    participantStatus: (
      conversationId: string,
      status: ConversationParticipantStatus,
    ): Promise<any> => {
      return this.transport.queue(
        new HttpTransaction({
          method: 'post',
          url: `${config.COMMUNICATION_SERVICE_URL}conversation/${conversationId}/participant/status`,
          body: { name: status, effectiveAt: new Date() },
        }),
      )
    },

    getParticipantByPhoneNumber: (
      phoneNumbers: string[],
    ): Promise<ConversationParticipantCodable[]> => {
      return this.transport.queue(
        new HttpTransaction({
          method: 'post',
          url: `${config.COMMUNICATION_SERVICE_URL}conversations/participants/phoneNumber`,
          body: phoneNumbers,
          retry: true,
        }),
      )
    },

    fetchActiveViewersForConversations: (
      conversationIds: string[],
    ): Promise<ConversationsViewersResult> => {
      return this.transport.queue(
        new HttpTransaction({
          url: `${config.COMMUNICATION_SERVICE_URL}conversation/viewers`,
          query: { conversationIds: conversationIds.join(',') },
        }),
      )
    },

    viewersForConversation: (conversationId: string): Promise<Viewer[]> => {
      return this.transport.queue(
        new HttpTransaction({
          method: 'get',
          url: `${config.COMMUNICATION_SERVICE_URL}conversation/${conversationId}/viewers`,
        }),
      )
    },

    agent: {
      getStatus: (conversationId: string): Promise<ConversationAgentStatus> => {
        return this.transport.queue(
          new HttpTransaction({
            url: `${config.COMMUNICATION_SERVICE_URL}conversations/${conversationId}/agent`,
            method: 'GET',
          }),
        )
      },
      toggleStatus: (
        conversationId: string,
        body: PutConversationAgentStatusRequest,
      ): Promise<ConversationAgentStatus> => {
        return this.transport.queue(
          new HttpTransaction({
            url: `${config.COMMUNICATION_SERVICE_URL}conversations/${conversationId}/agent`,
            method: 'PUT',
            body,
          }),
        )
      },
    },

    markdown: (conversationId: string, timeZone: string): Promise<MarkdownResult> => {
      return this.transport.queue(
        new HttpTransaction({
          method: 'get',
          url: `${config.COMMUNICATION_SERVICE_URL}conversation/${conversationId}/markdown`,
          query: { timeZone },
        }),
      )
    },
  }

  messaging = {
    suggestions: (
      conversationId: string,
      prompt: string,
    ): Promise<MessagingSuggestionResult> => {
      return this.transport.queue(
        new HttpTransaction({
          method: 'POST',
          url: `${config.MESSAGING_SERVICE_URL}lang/suggestions`,
          body: {
            conversationId,
            prompt,
          },
        }),
      )
    },
  }

  ivr = {
    get: (phoneNumberId: string): Promise<IvrSettings> => {
      return this.transport.queue(
        new HttpTransaction({
          url: `${config.COMMUNICATION_SERVICE_URL}admin/ivr/${phoneNumberId}`,
        }),
      )
    },

    set: (settings: IvrSettings): Promise<IvrSettings> => {
      return this.transport.queue(
        new HttpTransaction({
          method: 'post',
          url: `${config.COMMUNICATION_SERVICE_URL}admin/ivr`,
          body: settings,
        }),
      )
    },

    delete: (phoneNumberId: string): Promise<any> => {
      return this.transport.queue(
        new HttpTransaction({
          method: 'delete',
          url: `${config.COMMUNICATION_SERVICE_URL}admin/ivr/${phoneNumberId}`,
        }),
      )
    },
  }

  phoneNumber = {
    agent: {
      getStatus: (phoneNumberId: string): Promise<PhoneNumberAgentStatus> => {
        return this.transport.queue(
          new HttpTransaction({
            method: 'GET',
            url: `${config.COMMUNICATION_SERVICE_URL}phoneNumber/${phoneNumberId}/agent`,
          }),
        )
      },
      updateStatus: (
        phoneNumberId: string,
        body: UpdatePhoneNumberAgentRequest,
      ): Promise<PhoneNumberAgentStatus> => {
        return this.transport.queue(
          new HttpTransaction({
            method: 'PUT',
            url: `${config.COMMUNICATION_SERVICE_URL}phoneNumber/${phoneNumberId}/agent`,
            body,
          }),
        )
      },
      // Paginated equivalent of getStatus, scoped server-side to the phone numbers the caller can
      // see. Use this instead of calling getStatus once per number.
      getStatuses: (query: {
        maxResults: number
        pageToken?: string
      }): Promise<Paginated<PhoneNumberAgentStatus>> => {
        return this.transport.queue(
          new HttpTransaction({
            method: 'GET',
            url: `${config.COMMUNICATION_SERVICE_URL}phoneNumber/agent-status`,
            query,
          }),
        )
      },
    },
  }

  reactions = {
    post: (reaction: ActivityEncodableReaction): Promise<void> => {
      return this.transport.queue(
        new HttpTransaction({
          method: 'post',
          url: `${config.COMMUNICATION_SERVICE_URL}reaction`,
          body: reaction,
        }),
      )
    },

    delete: (id: string): Promise<void> => {
      return this.transport.queue(
        new HttpTransaction({
          method: 'delete',
          url: `${config.COMMUNICATION_SERVICE_URL}reaction/${id}`,
        }),
      )
    },
  }

  tags = {
    listByPhoneNumberId: ({
      phoneNumberId,
      query,
    }: PaginatedTagsRequest): Promise<PaginatedTagsResponse> => {
      const params = new URLSearchParams({
        ...(query?.since && { since: query.since.toISOString() }),
        ...(query?.maxResults && { maxResults: query.maxResults.toString() }),
        ...(query?.pageToken && { pageToken: query.pageToken }),
        ...(query?.includeCallsLabeled && {
          includeCallsLabeled: query.includeCallsLabeled.toString(),
        }),
      })

      return this.transport.queue(
        new HttpTransaction({
          method: 'GET',
          url: `${
            config.COMMUNICATION_SERVICE_URL
          }labels/v2/phone-numbers/${phoneNumberId}?${params.toString()}`,
        }),
      )
    },
    fetchDeletedTags: ({
      phoneNumberId,
      query,
    }: PaginatedTagsRequest): Promise<PaginatedTagsResponse> => {
      const params = new URLSearchParams({
        ...(query?.since && { since: query.since.toISOString() }),
        ...(query?.maxResults && { maxResults: query.maxResults.toString() }),
        ...(query?.pageToken && { pageToken: query.pageToken }),
      })

      return this.transport.queue(
        new HttpTransaction({
          method: 'GET',
          url: `${
            config.COMMUNICATION_SERVICE_URL
          }labels/phone-numbers/${phoneNumberId}/deleted?${params.toString()}`,
        }),
      )
    },
    create: (
      tag: Pick<CodableTag, 'name' | 'description' | 'emoji' | 'phoneNumberId'>,
    ): Promise<CodableTag> => {
      return this.transport.queue(
        new HttpTransaction({
          method: 'post',
          url: `${config.COMMUNICATION_SERVICE_URL}labels`,
          body: tag,
        }),
      )
    },
    update: (
      tag: Pick<CodableTag, 'id' | 'name' | 'description' | 'emoji'>,
    ): Promise<CodableTag> => {
      return this.transport.queue(
        new HttpTransaction({
          method: 'put',
          url: `${config.COMMUNICATION_SERVICE_URL}labels/${tag.id}`,
          body: tag,
        }),
      )
    },
    toggle: (tagId: string, enabled: boolean) => {
      return this.transport.queue(
        new HttpTransaction({
          method: 'put',
          url: `${config.COMMUNICATION_SERVICE_URL}labels/${tagId}/toggle`,
          body: { enabled },
        }),
      )
    },
    deleteTagFromActivity: ({
      activityId,
      tagId,
    }: {
      activityId: string
      tagId: string
    }) => {
      return this.transport.queue(
        new HttpTransaction({
          method: 'delete',
          url: `${config.COMMUNICATION_SERVICE_URL}activity/${activityId}/labels/${tagId}`,
        }),
      )
    },
    delete: (tagId: string) => {
      return this.transport.queue(
        new HttpTransaction({
          method: 'delete',
          url: `${config.COMMUNICATION_SERVICE_URL}labels/${tagId}`,
        }),
      )
    },
  }

  comments = {
    post: (comment: EncodableComment): Promise<any> => {
      return this.transport.queue(
        new HttpTransaction({
          method: 'post',
          url: `${config.COMMUNICATION_SERVICE_URL}comment`,
          body: comment,
        }),
      )
    },

    delete: (id: string): Promise<any> => {
      return this.transport.queue(
        new HttpTransaction({
          method: 'delete',
          url: `${config.COMMUNICATION_SERVICE_URL}comment/${id}`,
        }),
      )
    },
  }

  scheduledMessages = {
    count: (
      params: GetScheduledMessagesCountRequest,
      signal?: AbortSignal,
    ): Promise<GetScheduledMessagesCountResponse> => {
      return this.transport.queue(
        new HttpTransaction({
          method: 'get',
          url: `${config.COMMUNICATION_SERVICE_URL}scheduledMessage/count`,
          query: params,
          signal,
        }),
      )
    },

    send: (
      params: PostScheduledMessageRequest,
    ): Promise<PostScheduledMessageResponse> => {
      return this.transport.queue(
        new HttpTransaction({
          method: 'post',
          url: `${config.COMMUNICATION_SERVICE_URL}scheduledMessage`,
          body: params,
        }),
      )
    },

    delete: ({
      id,
    }: DeleteScheduledMessageRequest): Promise<DeleteScheduledMessageResponse> => {
      return this.transport.queue(
        new HttpTransaction({
          method: 'delete',
          url: `${config.COMMUNICATION_SERVICE_URL}scheduledMessage/${id}`,
        }),
      )
    },

    conversation: {
      listPaginated: (
        conversationId: string,
        params: GetScheduledMessagesByConversationRequest,
      ): Promise<GetScheduledMessagesByConversationResponse> => {
        return this.transport.queue(
          new HttpTransaction({
            method: 'get',
            url: `${config.COMMUNICATION_SERVICE_URL}scheduledMessage/conversations/${conversationId}`,
            query: params,
          }),
        )
      },
    },
  }

  upload(
    file: File,
    onProgress?: (loaded: number, total: number) => void,
    options?: UploadOptions,
  ): Promise<string> {
    // @ts-expect-error unchecked index access
    return this.transport.client
      .post<string, PostUploadRequest>(`${config.COMMUNICATION_SERVICE_URL}upload/url`, {
        type: file.type,
        ...options,
      })
      .then((url) => {
        const config: AxiosRequestConfig = {
          headers: {
            'Content-Type': file.type,
          },
          onUploadProgress: (progress: AxiosProgressEvent) => {
            if (onProgress && typeof progress.total === 'number') {
              onProgress(progress.loaded, progress.total)
            }
          },
        }
        return this.transport.client.put(url, file, config).then(() => url.split('?')[0])
      })
  }

  private uploadToStorage(
    resourceType: 'message-media' | 'workflow' | 'file',
    file: File,
    onProgress?: (loaded: number, total: number) => void,
  ): Promise<string> {
    const form = new FormData()
    form.append('files', file)

    return this.transport.client
      .post<{ resources: { url: string }[] }, FormData>(
        `${config.STORAGE_PROXY_URL}resource/${resourceType}`,
        form,
        {
          onUploadProgress: (progress: AxiosProgressEvent) => {
            if (onProgress && typeof progress.total === 'number') {
              onProgress(progress.loaded, progress.total)
            }
          },
        },
      )
      .then((response) => {
        const url = response.resources?.[0]?.url
        if (!url) {
          throw new Error(`Storage ${resourceType} upload returned no url`)
        }
        return url
      })
  }

  /**
   * Uploads a message attachment to the storage service and returns the URL used to reference it.
   */
  uploadMessageMedia(
    file: File,
    onProgress?: (loaded: number, total: number) => void,
  ): Promise<string> {
    return this.uploadToStorage('message-media', file, onProgress)
  }

  /**
   * Uploads call-flow audio (greetings, IVR prompts, play-audio) to the storage service and returns the URL.
   */
  uploadWorkflowAudio(
    file: File,
    onProgress?: (loaded: number, total: number) => void,
  ): Promise<string> {
    return this.uploadToStorage('workflow', file, onProgress)
  }

  /**
   * Uploads a long-lived profile image (workspace logo, user/contact avatar) to the storage service and returns
   * the signed `/file/*` proxy URL used to reference it.
   */
  uploadProfileImage(
    file: File,
    onProgress?: (loaded: number, total: number) => void,
  ): Promise<string> {
    return this.uploadToStorage('file', file, onProgress)
  }
}
